import { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useApi } from '../hooks/useApi';
import { AccountPaths } from '../api/constants';
import { useApiHandleError } from '../hooks/useApiHandleError';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { AlertComponent } from './Alert';
import { Icon } from './Icon';
import { faLock, faClose, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { SpinnerComponent } from './Spinner';
import { PasswordRules } from '../../areas/auth/PwRuless';
import { isValidPassword } from '../utils/validators';

type Props = {
  show: boolean;
  onClose: () => void;
};

export function ChangePasswordModal({ show, onClose }: Props) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const API = useApi();
  const { setError, errorMessage } = useApiHandleError();
  const [successMessage, setSuccessMessage] = useState('');

  const handleClose = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setSuccessMessage('');
    setError('');
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (successMessage) setSuccessMessage('');
    if (errorMessage) setError('');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.currentPassword) {
      setError('Current password is required');
      return;
    }

    if (!isValidPassword(formData.newPassword)) {
      setError('New password does not meet requirements');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setSaving(true);
    try {
      const res = await API.post(AccountPaths.ChangePassword(), formData);
      setSuccessMessage(res.data.message || 'Password changed successfully');
      // Auto-close after 2 seconds on success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      setError(error);
    }
    setSaving(false);
  };

  const isFormValid =
    formData.currentPassword.trim() !== '' &&
    formData.newPassword.trim() !== '' &&
    formData.confirmPassword.trim() !== '' &&
    formData.newPassword === formData.confirmPassword &&
    isValidPassword(formData.newPassword);

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">
          <Icon icon={faLock} /> Change Password
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <AlertComponent
            variant={successMessage ? 'success' : 'danger'}
            message={errorMessage || successMessage}
          />

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Current Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPasswords.current ? "text" : "password"}
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter your current password"
                disabled={saving || Boolean(successMessage)}
                autoComplete="current-password"
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                disabled={saving || Boolean(successMessage)}
              >
                <Icon icon={showPasswords.current ? faEyeSlash : faEye} />
              </Button>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPasswords.new ? "text" : "password"}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter your new password"
                disabled={saving || Boolean(successMessage)}
                autoComplete="new-password"
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                disabled={saving || Boolean(successMessage)}
              >
                <Icon icon={showPasswords.new ? faEyeSlash : faEye} />
              </Button>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Confirm New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPasswords.confirm ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter your new password"
                disabled={saving || Boolean(successMessage)}
                autoComplete="new-password"
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                disabled={saving || Boolean(successMessage)}
              >
                <Icon icon={showPasswords.confirm ? faEyeSlash : faEye} />
              </Button>
            </InputGroup>
          </Form.Group>

          <PasswordRules value={formData.newPassword} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            disabled={saving}
            variant="secondary"
            onClick={handleClose}
          >
            <Icon icon={faClose} /> Cancel
          </Button>
          <Button
            disabled={!isFormValid || saving || Boolean(successMessage || errorMessage)}
            variant="success"
            type="submit"
          >
            {saving ? <SpinnerComponent /> : <Icon icon={faLock} />} Change Password
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
